/**
 * Модуль для управления экраном прогресса (Pass/Progress)
 */

import { logger } from "../../utils/logger.js";

/**
 * Определение тиров прогресса
 */
export const PROGRESS_TIERS = [
  { tier: 1, icon: '💰', name: 'Монеты', amount: 100, type: 'coins', free: true, premium: false },
  { tier: 2, icon: '⚡', name: 'Энергия', amount: 20, type: 'power', free: true, premium: false },
  { tier: 3, icon: '🎁', name: 'Коробка', amount: 1, type: 'box', free: true, premium: false },
  { tier: 4, icon: '⭐', name: 'Звёзды', amount: 10, type: 'stars', free: false, premium: true },
  { tier: 5, icon: '💰', name: 'Монеты', amount: 150, type: 'coins', free: true, premium: false },
  { tier: 6, icon: '🎴', name: 'Существо', amount: 1, type: 'creature', free: false, premium: true },
  { tier: 7, icon: '🎁', name: 'Большая коробка', amount: 1, type: 'big_box', free: false, premium: true },
  { tier: 8, icon: '⭐', name: 'Звёзды', amount: 20, type: 'stars', free: false, premium: true },
  { tier: 9, icon: '💰', name: 'Монеты', amount: 200, type: 'coins', free: true, premium: false },
  { tier: 10, icon: '🏆', name: 'Эксклюзивный скин', amount: 1, type: 'skin', free: false, premium: true },
];

/**
 * Создаёт менеджер экрана прогресса
 */
export function createProgressPassManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  documentRef = document
}) {
  let currentTier = 1;
  let claimedTiers = [];
  let currentTab = 'free';
  let hasPremiumPass = false;

  /**
   * Загружает прогресс пользователя
   */
  async function loadProgressData() {
    const user = getCurrentUser();
    if (!user) return;

    // Синхронизируем статус премиум-пасса из объекта пользователя
    if (user.premium_pass_active === true) {
      hasPremiumPass = true;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/progress`, {
        method: 'GET',
        headers: buildAuthHeaders()
      });

      if (response.success && response.data?.success) {
        const data = response.data;
        currentTier = data.currentTier || 1;
        claimedTiers = data.claimedTiers || [];
        // Используем данные из API, но приоритет у локального статуса пользователя
        hasPremiumPass = user.premium_pass_active === true || data.hasPremiumPass || false;
        logger.debug('[Progress] Loaded progress:', { currentTier, claimedTiers, hasPremiumPass });
      } else {
        // Если API не реализован, используем локальные данные
        // Можно получить из достижений или других источников
        const stats = await getUserStats();
        currentTier = calculateTierFromStats(stats);
        // Не сбрасываем claimedTiers, оставляем текущие значения
        if (claimedTiers.length === 0) {
          claimedTiers = [];
        }
        logger.warn('[Progress] API error, using local stats:', response.error || 'unknown');
      }
    } catch (error) {
      logger.error('[Progress] Error loading progress:', error);
      // Не сбрасываем claimedTiers при ошибке, сохраняем текущие значения
      if (currentTier === undefined || currentTier === null) {
        currentTier = 1;
      }
      // claimedTiers сохраняем, не сбрасываем
    }
  }

  /**
   * Получает статистику пользователя для расчёта тира
   */
  async function getUserStats() {
    const user = getCurrentUser();
    if (!user) return { battles: 0, wins: 0, creatures: 0 };

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/stats`, {
        method: 'GET',
        headers: buildAuthHeaders()
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      logger.error('[Progress] Error loading stats:', error);
    }

    return { battles: 0, wins: 0, creatures: 0 };
  }

  /**
   * Вычисляет текущий тир на основе статистики
   */
  function calculateTierFromStats(stats) {
    // Простая формула: каждые 10 боёв = +1 тир
    const battles = stats.battles || 0;
    const wins = stats.wins || 0;
    const creatures = stats.creatures || 0;
    
    const tierFromBattles = Math.floor(battles / 10) + 1;
    const tierFromWins = Math.floor(wins / 5) + 1;
    const tierFromCreatures = Math.floor(creatures / 3) + 1;
    
    return Math.min(10, Math.max(1, Math.max(tierFromBattles, tierFromWins, tierFromCreatures)));
  }

  /**
   * Рендерит тиры прогресса
   */
  function renderTiers() {
    const domCache = window.__domCacheManager;
    const tierGrid = domCache?.getCachedElement('#tier-grid', documentRef) 
      || documentRef.getElementById('tier-grid');
    
    if (!tierGrid) return;

    tierGrid.innerHTML = '';

    PROGRESS_TIERS.forEach(tier => {
      const tierEl = documentRef.createElement('div');
      
      let tierClass = 'tier-item';
      const isLocked = tier.tier > currentTier;
      const isCurrent = tier.tier === currentTier;
      const isClaimed = claimedTiers.includes(tier.tier);
      const isPremiumTier = tier.premium && !tier.free;
      const canClaim = tier.tier <= currentTier && !isClaimed && (!isPremiumTier || hasPremiumPass);
      const isAvailable = canClaim;
      
      // Проверяем доступность для текущего таба
      const isVisible = currentTab === 'free' ? tier.free : tier.premium;
      
      if (!isVisible) {
        tierEl.style.display = 'none';
      }
      
      if (isLocked) {
        tierClass += ' locked';
      } else if (isCurrent) {
        tierClass += ' current';
      } else if (isClaimed) {
        tierClass += ' claimed';
      } else if (isPremiumTier && !hasPremiumPass) {
        tierClass += ' premium-locked';
      }

      tierEl.className = tierClass;
      tierEl.onclick = () => {
        if (isAvailable) {
          claimTier(tier.tier);
        } else if (isPremiumTier && !hasPremiumPass) {
          const telegramWebApp = window.Telegram?.WebApp;
          if (telegramWebApp?.showAlert) {
            telegramWebApp.showAlert('Для получения этой награды требуется премиум-пасс');
          }
        }
      };

      tierEl.innerHTML = `
        <div class="tier-number">ТИР ${tier.tier}</div>
        <div class="tier-reward">${tier.icon}</div>
        <div class="tier-name">${tier.name}</div>
        ${tier.amount > 1 ? `<div class="tier-amount">x${tier.amount}</div>` : ''}
        ${isAvailable ? '<div class="tier-claim-badge">Получить</div>' : ''}
        ${isPremiumTier && !hasPremiumPass && !isLocked && !isClaimed ? '<div class="tier-premium-badge" style="position: absolute; top: 4px; right: 4px; background: #9c27b0; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700;">ПРЕМИУМ</div>' : ''}
      `;

      tierGrid.appendChild(tierEl);
    });
  }

  /**
   * Получает награду за тир
   */
  async function claimTier(tierNumber) {
    const tier = PROGRESS_TIERS.find(t => t.tier === tierNumber);
    if (!tier) return;

    if (tier.tier > currentTier) {
      const telegramWebApp = window.Telegram?.WebApp;
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Достигни этого тира!');
      }
      return;
    }

    if (claimedTiers.includes(tierNumber)) {
      const telegramWebApp = window.Telegram?.WebApp;
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Награда уже получена!');
      }
      return;
    }
    
    // Проверяем премиум-статус для премиум-наград
    if (tier.premium && !tier.free && !hasPremiumPass) {
      const telegramWebApp = window.Telegram?.WebApp;
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Для получения этой награды требуется премиум-пасс');
      }
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/progress/claim`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier: tierNumber })
      });

      if (response.success && response.data?.success) {
        const data = response.data;
        // Добавляем тир в список полученных только если его там еще нет
        if (!claimedTiers.includes(tierNumber)) {
          claimedTiers.push(tierNumber);
        }
        
        // Обновляем данные пользователя, если они пришли с сервера
        if (data.user) {
          const currentUser = getCurrentUser();
          if (currentUser) {
            const updatedUser = { ...currentUser, ...data.user };
            if (setCurrentUser) {
              setCurrentUser(updatedUser);
            }
            updateUserBalancesUI(updatedUser);
          } else {
            // Если нет текущего пользователя, обновляем из ответа
            updateUserBalancesUI(data.user);
          }
        } else {
          // Если данные пользователя не пришли, обновляем из текущего состояния
          const currentUser = getCurrentUser();
          if (currentUser) {
            updateUserBalancesUI(currentUser);
          }
        }

        renderTiers();
        
        const telegramWebApp = window.Telegram?.WebApp;
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(`Получено: ${tier.icon} ${tier.name} x${tier.amount}`);
        }
      } else {
        // Если API вернул ошибку (например, уже получен), показываем сообщение
        const errorCode = response.error || response.data?.error;
        if (errorCode === 'tier_already_claimed') {
          // Добавляем в список, чтобы не показывать кнопку "Получить"
          if (!claimedTiers.includes(tierNumber)) {
            claimedTiers.push(tierNumber);
          }
          renderTiers();
          const telegramWebApp = window.Telegram?.WebApp;
          if (telegramWebApp?.showAlert) {
            telegramWebApp.showAlert('Награда уже получена!');
          }
        } else {
          // Другая ошибка - применяем локально только если это не критично
          logger.warn('[Progress] API error, applying locally:', errorCode);
          applyRewardLocally(tier);
          if (!claimedTiers.includes(tierNumber)) {
            claimedTiers.push(tierNumber);
          }
          renderTiers();
        }
      }
    } catch (error) {
      logger.error('[Progress] Error claiming tier:', error);
      // Не применяем награду локально при ошибке - это может привести к дублированию
      // Показываем ошибку пользователю
      const telegramWebApp = window.Telegram?.WebApp;
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Ошибка при получении награды. Попробуйте позже.');
      }
    }
  }

  /**
   * Применяет награду локально (если API не доступен)
   */
  function applyRewardLocally(tier) {
    // Обновляем UI балансов (реальная награда будет применена на сервере)
    updateUserBalancesUI();
    
    const telegramWebApp = window.Telegram?.WebApp;
    if (telegramWebApp?.showAlert) {
      telegramWebApp.showAlert(`Получено: ${tier.icon} ${tier.name} x${tier.amount}`);
    }
  }

  /**
   * Обновляет кнопку премиум-пасса и секцию
   */
  function updatePremiumButton() {
    const premiumBtn = documentRef.querySelector('.premium-btn');
    const premiumSection = documentRef.querySelector('.premium-section');
    if (!premiumBtn) return;
    
    const user = getCurrentUser();
    const hasPremium = user?.premium_pass_active === true || hasPremiumPass;
    
    if (hasPremium) {
      // Если премиум-пасс активен, меняем вид кнопки и секции
      premiumBtn.textContent = '⭐ ПРЕМИУМ АКТИВЕН';
      premiumBtn.style.background = '#4dff4d';
      premiumBtn.style.opacity = '0.8';
      premiumBtn.style.cursor = 'default';
      premiumBtn.onclick = () => {
        const telegramWebApp = window.Telegram?.WebApp;
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert('У вас уже есть активный премиум-пасс!');
        }
      };
      
      // Обновляем заголовок секции
      const premiumTitle = premiumSection?.querySelector('.premium-title');
      if (premiumTitle) {
        premiumTitle.textContent = '⭐ Премиум Прогресс Активен';
        premiumTitle.style.color = '#4dff4d';
      }
      
      // Обновляем список преимуществ
      const premiumFeatures = premiumSection?.querySelectorAll('.premium-feature');
      if (premiumFeatures && premiumFeatures.length > 0) {
        premiumFeatures[0].textContent = '✅ Эксклюзивные существа доступны';
        if (premiumFeatures[1]) premiumFeatures[1].textContent = '✅ Двойные награды активны';
        if (premiumFeatures[2]) premiumFeatures[2].textContent = '✅ Все премиум-тиры разблокированы';
      }
    } else {
      // Если премиум-пасс не активен, показываем стандартный вид
      premiumBtn.textContent = 'ПОЛУЧИТЬ ЗА ⭐ 169';
      premiumBtn.style.background = '';
      premiumBtn.style.opacity = '1';
      premiumBtn.style.cursor = 'pointer';
      premiumBtn.onclick = () => {
        if (typeof window.buyPremiumPass === 'function') {
          window.buyPremiumPass();
        }
      };
      
      // Восстанавливаем стандартный вид секции
      const premiumTitle = premiumSection?.querySelector('.premium-title');
      if (premiumTitle) {
        premiumTitle.textContent = 'Разблокировать Премиум Прогресс';
        premiumTitle.style.color = '';
      }
      
      const premiumFeatures = premiumSection?.querySelectorAll('.premium-feature');
      if (premiumFeatures && premiumFeatures.length > 0) {
        premiumFeatures[0].textContent = '⭐ Эксклюзивные существа';
        if (premiumFeatures[1]) premiumFeatures[1].textContent = '💎 +50 Звёзд мгновенно';
        if (premiumFeatures[2]) premiumFeatures[2].textContent = '🎁 Двойные награды';
      }
    }
  }

  /**
   * Загружает экран прогресса
   */
  async function loadProgressScreen() {
    await loadProgressData();
    renderTiers();
    updateTimer();
    updatePremiumButton();
  }

  /**
   * Обновляет таймер сезона
   */
  function updateTimer() {
    const timerEl = documentRef.getElementById('pass-timer');
    if (!timerEl) return;

    // Вычисляем оставшиеся дни (30 дней от текущей даты)
    const seasonStart = new Date();
    seasonStart.setDate(1); // Первый день месяца
    const seasonEnd = new Date(seasonStart);
    seasonEnd.setMonth(seasonEnd.getMonth() + 1);
    
    const now = new Date();
    const daysLeft = Math.ceil((seasonEnd - now) / (1000 * 60 * 60 * 24));
    
    timerEl.textContent = `${Math.max(0, daysLeft)} ДНЕЙ ОСТАЛОСЬ`;
  }

  /**
   * Переключает таб (free/premium)
   */
  function switchTab(tabName) {
    currentTab = tabName;
    const tabs = documentRef.querySelectorAll('.pass-tab');
    tabs.forEach(tab => tab.classList.remove('active'));
    const activeTab = documentRef.getElementById(`pass-tab-${tabName}`);
    if (activeTab) {
      activeTab.classList.add('active');
    }
    renderTiers();
  }

  return {
    loadProgressScreen,
    claimTier,
    switchTab,
    renderTiers
  };
}



